import { invokeApi } from "../../bl_libs/invokeApi";

const getAuthHeader = () => ({
  "x-sh-auth": localStorage.getItem("token"),
});

// GET /api/tutors/school/instructors — Owner/Manager only, every instructor
// at the caller's own school (scoped server-side via getEffectiveSchoolId).
export const get_school_instructors_api = async () => {
  const requestObj = {
    path: `api/tutors/school/instructors`,
    method: "GET",
    headers: getAuthHeader(),
  };
  return invokeApi(requestObj);
};

// PUT /api/tutors/instructors/:instructorId/profile — admin-on-behalf-of-
// instructor edit (bio/photoUrl/subjects/isBookable). Counterpart to the
// instructor's own self-service PUT /api/tutors/profile below.
export const update_instructor_profile_api = async (instructorId, data) => {
  const requestObj = {
    path: `api/tutors/instructors/${instructorId}/profile`,
    method: "PUT",
    headers: getAuthHeader(),
    postData: data,
  };
  return invokeApi(requestObj);
};

// ============ Self-service (the logged-in instructor's own profile) ============
// Scoped server-side to req.user._id — no instructorId in the URL. Some
// schools run more freelance/marketplace-style than others, so instructors
// shouldn't have to go through an Owner/Manager just to update their own
// bio or photo.

export const get_my_tutoring_profile_api = async () => {
  const requestObj = {
    path: `api/tutors/profile`,
    method: "GET",
    headers: getAuthHeader(),
  };
  return invokeApi(requestObj);
};

export const update_my_tutoring_profile_api = async (data) => {
  const requestObj = {
    path: `api/tutors/profile`,
    method: "PUT",
    headers: getAuthHeader(),
    postData: data,
  };
  return invokeApi(requestObj);
};

// POST /api/tutors/profile/photo — multipart, field name "photo"
export const upload_my_tutoring_photo_api = async (formData) => {
  const requestObj = {
    path: `api/tutors/profile/photo`,
    method: "POST",
    headers: getAuthHeader(),
    postData: formData,
  };
  return invokeApi(requestObj);
};

// ============ Instructor booking inbox (accept/decline lesson requests) ============
// Scoped server-side to req.user._id (the logged-in instructor) — same
// self-service pattern as the profile calls above.

// GET /api/tutors/bookings/instructor?status= — the instructor's own
// requests/lessons, optionally filtered by status.
export const get_instructor_bookings_api = async (status) => {
  const requestObj = {
    path: `api/tutors/bookings/instructor`,
    method: "GET",
    headers: getAuthHeader(),
    ...(status ? { queryParams: { status } } : {}),
  };
  return invokeApi(requestObj);
};

// POST /api/tutors/bookings/:id/respond — Body: { decision: "accept" | "decline" }.
// Accept captures the held payment and confirms the lesson; decline voids
// the hold and reopens the slot. Both are final — there's no "undo".
export const respond_to_booking_api = async (bookingId, decision) => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/respond`,
    method: "POST",
    headers: getAuthHeader(),
    postData: { decision },
  };
  return invokeApi(requestObj);
};

// GET /api/tutors/bookings/:id/video — signed Daily.co join token for a
// confirmed online lesson. Lazily creates the room if respond_to_booking's
// own creation attempt failed.
export const get_video_join_info_api = async (bookingId) => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/video`,
    method: "GET",
    headers: getAuthHeader(),
  };
  return invokeApi(requestObj);
};

// POST /api/tutors/bookings/:id/instructor-cancel — instructor-initiated
// cancellation of an already-confirmed lesson (declining covers the
// still-pending case; this is for after acceptance). Always a full refund.
export const instructor_cancel_booking_api = async (bookingId) => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/instructor-cancel`,
    method: "POST",
    headers: getAuthHeader(),
  };
  return invokeApi(requestObj);
};

// ── Proposing a session from a student record ────────────────────────────────
// The instructor's own free slots — working hours minus committed bookings,
// declared blocks and external-calendar busy time. Never a bare datetime field:
// that is how a CFI offers a slot they are already flying in.

export const get_my_slots_api = async ({ duration = 60, subject = "", from, to } = {}) => {
  const qs = new URLSearchParams({ duration: String(duration) });
  if (subject) qs.set("subject", subject);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const requestObj = {
    path: `api/tutors/my-slots?${qs.toString()}`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  };
  return invokeApi(requestObj);
};

/** data: { start_time, end_time, subject, note } — note is required server-side. */
export const propose_session_api = async (studentId, data) => {
  const requestObj = {
    path: `api/tutors/students/${studentId}/propose`,
    method: "POST",
    headers: { "x-sh-auth": localStorage.getItem("token") },
    postData: data,
  };
  return invokeApi(requestObj);
};

export const flag_weak_area_api = async (studentId, data) => {
  const requestObj = {
    path: `api/tutors/students/${studentId}/flag`,
    method: "POST",
    headers: { "x-sh-auth": localStorage.getItem("token") },
    postData: data,
  };
  return invokeApi(requestObj);
};

/** The instructor's own roster — the students they have actually taught. */
export const get_instructor_roster_api = async () => {
  const requestObj = {
    path: `api/tutors/roster`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  };
  return invokeApi(requestObj);
};

// ==================== Earnings over time ====================
// The detailed payout view: totals split by 72-hour hold, failures named, and
// a zero-filled series. Weekly by default — a CFI teaches ~2 sessions a week,
// so a daily series is mostly zeros and reads as "I earn nothing" rather than
// "I teach on Tuesdays".
export const get_earnings_detail_api = async ({ grain = "week", days } = {}) => {
  const qs = new URLSearchParams({ grain });
  if (days) qs.set("days", String(days));
  const requestObj = {
    path: `api/tutors/earnings/detail?${qs.toString()}`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  };
  return invokeApi(requestObj);
};

// ==================== The pre-session agenda ====================
// What this student is weak on, what happened last time, what is outstanding.
// Built long ago and reachable from nowhere until now — the spec calls it the
// reason an instructor never walks in cold.
export const get_session_agenda_api = async (bookingId) => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/agenda`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  };
  return invokeApi(requestObj);
};

/** data: { body, createFollowUp?, acsCodes? } */
export const post_session_summary_api = async (bookingId, data) => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/summary`,
    method: "POST",
    headers: { "x-sh-auth": localStorage.getItem("token") },
    postData: data,
  };
  return invokeApi(requestObj);
};

// ── the three booking actions that had no client ─────────────────────────────

/**
 * POST /api/tutors/bookings/:id/reschedule
 *
 * NOT cancel-and-rebook: moving the same row keeps the money, the credit and
 * the identity intact, and the .ics keeps its UID and updates rather than
 * cancelling. Open to the instructor, the student, and — since the front desk
 * is who takes the "can we move Thursday" call — managers at the booking's own
 * school.
 */
export const reschedule_booking_api = async (bookingId, { start_time, end_time, reason = "" }) => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/reschedule`,
    method: "POST",
    headers: { "x-sh-auth": localStorage.getItem("token") },
    postData: { start_time, end_time, reason },
  };
  return invokeApi(requestObj);
};

/**
 * POST /api/tutors/bookings/:id/no-show   { who: "student"|"instructor", note? }
 *
 * Money moves on this: an INSTRUCTOR no-show returns the student's credit
 * immediately, because the student did nothing wrong and should not spend a
 * session on it. Nothing could trigger it before.
 */
export const mark_no_show_api = async (bookingId, who, note = "") => {
  const requestObj = {
    path: `api/tutors/bookings/${bookingId}/no-show`,
    method: "POST",
    headers: { "x-sh-auth": localStorage.getItem("token") },
    postData: { who, note },
  };
  return invokeApi(requestObj);
};

/** The instructor's own free windows, for the reschedule picker. */
export const get_my_slots_for_reschedule_api = async ({ duration = 60, subject = "" } = {}) => {
  const q = new URLSearchParams({ duration: String(duration), subject }).toString();
  const requestObj = {
    path: `api/tutors/my-slots?${q}`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  };
  return invokeApi(requestObj);
};

/**
 * Recording consent, both directions.
 *
 * The GET returns the STATEMENT ITSELF. That is deliberate on the server's part
 * and load-bearing here: consent is to a specific text, so a client that writes
 * its own wording is collecting agreement to something we did not say. Render
 * `statement.text` verbatim — never paraphrase it, never summarise it.
 */
export const get_recording_consent_api = async (bookingId) =>
  invokeApi({
    path: `api/tutors/bookings/${bookingId}/consent`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  });

export const post_recording_consent_api = async (bookingId, granted) =>
  invokeApi({
    path: `api/tutors/bookings/${bookingId}/consent`,
    method: "POST",
    headers: { "x-sh-auth": localStorage.getItem("token") },
    postData: { granted },
  });

/**
 * The one session worth interrupting somebody about — polled by the join
 * prompt. Answers null almost always, which is what makes polling it cheap.
 */
export const get_next_session_api = async () =>
  invokeApi({
    path: `api/tutors/sessions/next`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  });

/** What cancelling would cost, before anyone cancels. */
export const get_instructor_cancellation_quote_api = async (bookingId) =>
  invokeApi({
    path: `api/tutors/bookings/${bookingId}/instructor-cancellation-quote`,
    method: "GET",
    headers: { "x-sh-auth": localStorage.getItem("token") },
  });
